import UploadIcon from '@mui/icons-material/Upload';
import { Box, Menu, MenuItem } from '@mui/material';
import NewActionButton from '../Shared/NewActionButton';
import { getPlusMenuItems } from 'components/navigation/PlusButton/PlusButton';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  checkCreateAssetsPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { useSearch } from 'components/Search/SearchContextProvider';
import { ResultsViewToggle } from './ResultsViewToggle';
import { useExportImport } from 'components/ExportImport/ExportImportContext';
import { useAssetQuickLaunch } from 'components/editorial/AssetQuickLaunch/useAssetQuickLaunch';
import { extractAssetTypeFromPath } from 'components/editorial/AssetQuickLaunch/utils';

export const EditorialPreSearchContent = () => {
  const { isModalSearch } = useSearch();
  const { t } = useTranslation();
  const { activeSite, sessionConfig } = useCosmos();
  const navigate = useNavigate();
  const {
    import: { toggleImportModal },
  } = useExportImport();
  const [anchorElNewBtn, setAnchorElNewBtn] = useState<HTMLElement | null>(null);

  const {
    openQuickLaunch,
    modal: quickLaunchModal,
    isQuickLaunchAssetType,
  } = useAssetQuickLaunch();

  const menuItems = getPlusMenuItems({ activeSite, sessionConfig, t });
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanCreate, name: createPermName } =
    checkCreateAssetsPermission(sessionConfig);

  const newBtnState: PermissionInfo = useMemo(() => {
    if (userIsAdmin) {
      return {
        info: undefined,
        disabled: false,
      };
    }
    if (!userCanCreate) {
      return {
        info: t('blox.permissions.msg.required_short', { name: createPermName }),
        disabled: true,
      };
    }
    return {
      info: undefined,
      disabled: false,
    };
  }, [t, userCanCreate, createPermName, userIsAdmin]);

  const editorialApps = useMemo(() => {
    return Object.values(menuItems.editorial.items).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [menuItems]);

  const handleOpenNewMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNewBtn(event.currentTarget);
  };

  const handleCloseNewMenu = () => {
    setAnchorElNewBtn(null);
  };

  const strImport = t('blox.general.import');

  return (
    <Fragment>
      <Box
        id="editorial-pre-search-field-buttons"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        {!isModalSearch && (
          <>
            <NewActionButton
              id="editorial-search-new-asset-button"
              ariaLabel="new-asset-button"
              onClick={handleOpenNewMenu}
              open={Boolean(anchorElNewBtn)}
              disabled={newBtnState.disabled}
              tooltip={newBtnState.info}
            >
              {t('blox.general.new')}
            </NewActionButton>
            <Menu
              id="editorial-search-new-asset-menu"
              data-testid="editorial-search-new-asset-menu"
              anchorEl={anchorElNewBtn}
              open={Boolean(anchorElNewBtn)}
              onClose={handleCloseNewMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                  },
                },
                list: {
                  dense: true,
                },
              }}
            >
              {editorialApps.map((app) => {
                const Icon = app.icon;
                return (
                  <MenuItem
                    key={app.key}
                    onClick={() => {
                      handleCloseNewMenu();
                      if (app.path) {
                        const assetType = extractAssetTypeFromPath(app.path);
                        if (assetType && isQuickLaunchAssetType(assetType)) {
                          openQuickLaunch({
                            assetType,
                            assetLabel: app.label,
                            navigatePath: app.path,
                          });
                          return;
                        }
                        navigate(`${app.path}`);
                      }
                    }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {Icon && <Icon className="itemicon" />}
                      {app.label}
                    </Box>
                  </MenuItem>
                );
              })}
              <MenuItem
                key="import-asset"
                onClick={() => {
                  toggleImportModal();
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UploadIcon className="itemicon" />
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    {strImport}
                  </Box>
                </Box>
              </MenuItem>
            </Menu>
          </>
        )}

        <ResultsViewToggle />
      </Box>
      {quickLaunchModal}
    </Fragment>
  );
};
